import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/layout/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import {
  CalendarClock,
  Check,
  Copy,
  Loader2,
  Plus,
  Trash2,
  Pencil,
  CalendarX2,
  Clock,
  Link2,
  Code2,
  AlertTriangle,
} from "lucide-react";
import {
  type EventType,
  type DateOverride,
  getMyBookingPage,
  updateMyBookingPage,
  replaceAvailabilityRules,
  createDateOverride,
  deleteDateOverride,
  deleteEventType,
  getGoogleConnectUrl,
  disconnectGoogle,
} from "@/lib/bookingApi";
import { EventTypeEditor } from "./EventTypeEditor";

// ── Constants / helpers ───────────────────────────────────────────────────────

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const TIMEZONES = [
  { value: "America/New_York", label: "Eastern (America/New_York)" },
  { value: "America/Chicago", label: "Central (America/Chicago)" },
  { value: "America/Denver", label: "Mountain (America/Denver)" },
  { value: "America/Phoenix", label: "Arizona (America/Phoenix)" },
  { value: "America/Los_Angeles", label: "Pacific (America/Los_Angeles)" },
];

/** "09:00:00" or "09:00" → "09:00" for <input type="time">. */
const toHHMM = (t: string | null | undefined): string => (t ? t.slice(0, 5) : "");

/** "09:00" → "9:00 AM" for display. */
const formatHHMM = (t: string | null | undefined): string => {
  const hhmm = toHHMM(t);
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
};

const formatDate = (iso: string): string => {
  try {
    return new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

interface TimeRange {
  start: string; // "HH:MM"
  end: string;
}

type WeeklyHours = TimeRange[][]; // index = weekday 0–6

const emptyWeek = (): WeeklyHours => [[], [], [], [], [], [], []];

// ── Small copy button ─────────────────────────────────────────────────────────

function CopyButton({ text, label }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => {
        navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
    >
      {copied ? <Check className="w-3.5 h-3.5 mr-1 text-green-600" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
      {copied ? "Copied" : label ?? "Copy"}
    </Button>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function SchedulingSettings() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const myPageQuery = useQuery({
    queryKey: ["booking-my-page"],
    queryFn: getMyBookingPage,
    retry: 1,
  });

  // ── Google OAuth return params (?google=connected / ?google=error) ─────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const google = params.get("google");
    if (!google) return;
    if (google === "connected") {
      toast({ title: "Google Calendar connected", description: "Your Google free/busy will now block booking slots." });
      queryClient.invalidateQueries({ queryKey: ["booking-my-page"] });
    } else if (google === "error") {
      toast({
        title: "Google connection failed",
        description: params.get("reason") || "Something went wrong during the Google authorization.",
        variant: "destructive",
      });
    }
    params.delete("google");
    params.delete("reason");
    const qs = params.toString();
    window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Page header form state ──────────────────────────────────────────────────
  const [pageForm, setPageForm] = useState({
    slug: "",
    display_name: "",
    title: "",
    photo_url: "",
    timezone: "America/New_York",
    is_active: true,
  });
  const [pageSeeded, setPageSeeded] = useState(false);

  // ── Weekly hours state ──────────────────────────────────────────────────────
  const [week, setWeek] = useState<WeeklyHours>(emptyWeek());
  const [weekSeeded, setWeekSeeded] = useState(false);

  useEffect(() => {
    const data = myPageQuery.data;
    if (!data) return;
    if (!pageSeeded) {
      setPageForm({
        slug: data.page.slug,
        display_name: data.page.display_name,
        title: data.page.title ?? "",
        photo_url: data.page.photo_url ?? "",
        timezone: data.page.timezone,
        is_active: data.page.is_active,
      });
      setPageSeeded(true);
    }
    if (!weekSeeded) {
      const w = emptyWeek();
      for (const r of data.availabilityRules) {
        if (r.weekday >= 0 && r.weekday <= 6) {
          w[r.weekday].push({ start: toHHMM(r.start_time), end: toHHMM(r.end_time) });
        }
      }
      setWeek(w);
      setWeekSeeded(true);
    }
  }, [myPageQuery.data, pageSeeded, weekSeeded]);

  // ── Mutations ───────────────────────────────────────────────────────────────

  const savePageMutation = useMutation({
    mutationFn: () =>
      updateMyBookingPage({
        slug: pageForm.slug.trim(),
        display_name: pageForm.display_name.trim(),
        title: pageForm.title.trim() || null,
        photo_url: pageForm.photo_url.trim() || null,
        timezone: pageForm.timezone,
        is_active: pageForm.is_active,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["booking-my-page"] });
      toast({ title: "Booking page saved" });
    },
    onError: (err: Error) =>
      toast({ title: "Save failed", description: err.message, variant: "destructive" }),
  });

  const saveHoursMutation = useMutation({
    mutationFn: () => {
      const rules = week.flatMap((ranges, weekday) =>
        ranges.map((r) => ({ weekday, start_time: r.start, end_time: r.end }))
      );
      return replaceAvailabilityRules(rules);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["booking-my-page"] });
      toast({ title: "Weekly hours saved" });
    },
    onError: (err: Error) =>
      toast({ title: "Save failed", description: err.message, variant: "destructive" }),
  });

  const connectGoogleMutation = useMutation({
    mutationFn: getGoogleConnectUrl,
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: (err: Error) =>
      toast({ title: "Could not start Google connect", description: err.message, variant: "destructive" }),
  });

  const disconnectGoogleMutation = useMutation({
    mutationFn: disconnectGoogle,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["booking-my-page"] });
      toast({ title: "Google Calendar disconnected" });
    },
    onError: (err: Error) =>
      toast({ title: "Disconnect failed", description: err.message, variant: "destructive" }),
  });

  const deleteOverrideMutation = useMutation({
    mutationFn: (id: string) => deleteDateOverride(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["booking-my-page"] });
      toast({ title: "Override removed" });
    },
    onError: (err: Error) =>
      toast({ title: "Delete failed", description: err.message, variant: "destructive" }),
  });

  const deleteEventTypeMutation = useMutation({
    mutationFn: (id: string) => deleteEventType(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["booking-my-page"] });
      toast({ title: "Event type deleted" });
    },
    onError: (err: Error) =>
      toast({
        title: "Delete failed",
        description: err.message || "Event types with existing bookings cannot be deleted — deactivate them instead.",
        variant: "destructive",
      }),
  });

  // ── Date override dialog state ──────────────────────────────────────────────
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideForm, setOverrideForm] = useState({
    date: "",
    unavailable: true,
    start: "09:00",
    end: "12:00",
  });

  const createOverrideMutation = useMutation({
    mutationFn: () =>
      createDateOverride({
        date: overrideForm.date,
        is_unavailable: overrideForm.unavailable,
        ...(overrideForm.unavailable
          ? {}
          : { start_time: overrideForm.start, end_time: overrideForm.end }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["booking-my-page"] });
      setOverrideOpen(false);
      toast({ title: "Override added" });
    },
    onError: (err: Error) => {
      const duplicate =
        err.message.includes("already exists") || err.message.includes("409");
      toast({
        title: "Could not add override",
        description: duplicate ? "An override for that date already exists." : err.message,
        variant: "destructive",
      });
    },
  });

  // ── Event type editor / delete confirm state ────────────────────────────────
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingEventType, setEditingEventType] = useState<EventType | null>(null);
  const [deletingEventType, setDeletingEventType] = useState<EventType | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  // ── Weekly hours editing helpers ────────────────────────────────────────────

  const addRange = (day: number) =>
    setWeek((w) => w.map((ranges, i) => (i === day ? [...ranges, { start: "09:00", end: "17:00" }] : ranges)));

  const removeRange = (day: number, idx: number) =>
    setWeek((w) => w.map((ranges, i) => (i === day ? ranges.filter((_, j) => j !== idx) : ranges)));

  const updateRange = (day: number, idx: number, patch: Partial<TimeRange>) =>
    setWeek((w) =>
      w.map((ranges, i) =>
        i === day ? ranges.map((r, j) => (j === idx ? { ...r, ...patch } : r)) : ranges
      )
    );

  const hoursValid = week.every((ranges) =>
    ranges.every((r) => r.start && r.end && r.start < r.end)
  );

  const saveHours = () => {
    if (!hoursValid) {
      toast({
        title: "Check your hours",
        description: "Each time range needs a start time earlier than its end time.",
        variant: "destructive",
      });
      return;
    }
    saveHoursMutation.mutate();
  };

  // ── Derived ────────────────────────────────────────────────────────────────

  const data = myPageQuery.data;
  const slug = pageForm.slug || data?.page.slug || "";
  const publicUrl = `${window.location.origin}/book/${slug}`;
  const embedSnippet = `<iframe src="${window.location.origin}/book/${slug}?embed=1" width="100%" height="700" frameborder="0"></iframe>`;

  const locationLabel = (lt: EventType["location_type"]) =>
    lt === "zoom" ? "Zoom" : lt === "phone" ? "Phone" : "In person";

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl">
        <div>
          <h1 className="text-3xl font-heading font-bold flex items-center gap-2">
            <CalendarClock className="w-7 h-7 text-primary" />
            Scheduling
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Your personal booking page — meeting types, weekly hours, and calendar connections.
          </p>
        </div>

        {myPageQuery.isLoading && (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        )}

        {myPageQuery.isError && (
          <Card className="border-amber-300 bg-amber-50 dark:bg-amber-950/20">
            <CardContent className="flex flex-col items-center justify-center py-12 text-center gap-3">
              <AlertTriangle className="w-8 h-8 text-amber-500" />
              <div>
                <p className="font-semibold">Booking isn't available yet</p>
                <p className="text-sm text-muted-foreground mt-1 max-w-md">
                  The booking tables haven't been provisioned on this server yet, or the
                  service is temporarily unavailable. Please try again later.
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => myPageQuery.refetch()}>
                Try again
              </Button>
            </CardContent>
          </Card>
        )}

        {data && (
          <>
            {/* ── 1. Page header ─────────────────────────────────────────────── */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Link2 className="w-5 h-5 text-primary" /> Booking Page
                </CardTitle>
                <CardDescription>How your public page looks and where it lives.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="bp-slug">URL slug</Label>
                    <div className="flex gap-2">
                      <Input
                        id="bp-slug"
                        value={pageForm.slug}
                        onChange={(e) => setPageForm((f) => ({ ...f, slug: e.target.value }))}
                      />
                      <CopyButton text={publicUrl} label="Copy link" />
                    </div>
                    <p className="text-xs text-muted-foreground break-all">{publicUrl}</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="bp-name">Display name</Label>
                    <Input
                      id="bp-name"
                      value={pageForm.display_name}
                      onChange={(e) => setPageForm((f) => ({ ...f, display_name: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="bp-title">Title</Label>
                    <Input
                      id="bp-title"
                      value={pageForm.title}
                      onChange={(e) => setPageForm((f) => ({ ...f, title: e.target.value }))}
                      placeholder="e.g. CPA, Tax Manager"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="bp-photo">Photo URL</Label>
                    <Input
                      id="bp-photo"
                      value={pageForm.photo_url}
                      onChange={(e) => setPageForm((f) => ({ ...f, photo_url: e.target.value }))}
                      placeholder="https://…"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Timezone</Label>
                    <Select
                      value={pageForm.timezone}
                      onValueChange={(v) => setPageForm((f) => ({ ...f, timezone: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TIMEZONES.map((tz) => (
                          <SelectItem key={tz.value} value={tz.value}>
                            {tz.label}
                          </SelectItem>
                        ))}
                        {!TIMEZONES.some((tz) => tz.value === pageForm.timezone) && (
                          <SelectItem value={pageForm.timezone}>{pageForm.timezone}</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-3 pt-6">
                    <Switch
                      id="bp-active"
                      checked={pageForm.is_active}
                      onCheckedChange={(v) => setPageForm((f) => ({ ...f, is_active: v }))}
                    />
                    <Label htmlFor="bp-active" className="cursor-pointer">
                      Page active — accepting bookings
                    </Label>
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button onClick={() => savePageMutation.mutate()} disabled={savePageMutation.isPending}>
                    {savePageMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Save page
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* ── 2. Google Calendar ─────────────────────────────────────────── */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <CalendarClock className="w-5 h-5 text-primary" /> Google Calendar
                </CardTitle>
                <CardDescription>
                  Read-only free/busy access — events on your Google calendar block matching
                  booking slots. We never see event details and never write to your calendar.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {data.googleConnected ? (
                  <div className="flex items-center gap-3">
                    <Badge className="bg-green-100 text-green-800 hover:bg-green-100 border border-green-300">
                      <Check className="w-3 h-3 mr-1" /> Connected
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setConfirmDisconnect(true)}
                      disabled={disconnectGoogleMutation.isPending}
                    >
                      Disconnect
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={() => connectGoogleMutation.mutate()}
                    disabled={connectGoogleMutation.isPending}
                  >
                    {connectGoogleMutation.isPending && (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    )}
                    Connect Google Calendar
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* ── 3. Weekly hours ────────────────────────────────────────────── */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Clock className="w-5 h-5 text-primary" /> Weekly Hours
                </CardTitle>
                <CardDescription>
                  When you're available each week, in your timezone ({pageForm.timezone}).
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {WEEKDAYS.map((dayName, day) => (
                  <div key={day} className="flex items-start gap-3 py-1.5 border-b last:border-b-0">
                    <div className="w-24 pt-1.5 text-sm font-medium shrink-0">{dayName}</div>
                    <div className="flex-1 space-y-2">
                      {week[day].length === 0 && (
                        <p className="text-sm text-muted-foreground pt-1.5">Unavailable</p>
                      )}
                      {week[day].map((r, idx) => (
                        <div key={idx} className="flex items-center gap-2 flex-wrap">
                          <Input
                            type="time"
                            value={r.start}
                            onChange={(e) => updateRange(day, idx, { start: e.target.value })}
                            className="w-32 h-8"
                          />
                          <span className="text-muted-foreground text-sm">to</span>
                          <Input
                            type="time"
                            value={r.end}
                            onChange={(e) => updateRange(day, idx, { end: e.target.value })}
                            className="w-32 h-8"
                          />
                          {r.start && r.end && r.start >= r.end && (
                            <span className="text-xs text-destructive">start must be before end</span>
                          )}
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            onClick={() => removeRange(day, idx)}
                            title="Remove range"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="shrink-0"
                      onClick={() => addRange(day)}
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add
                    </Button>
                  </div>
                ))}
                <div className="flex justify-end pt-2">
                  <Button onClick={saveHours} disabled={saveHoursMutation.isPending}>
                    {saveHoursMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Save weekly hours
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* ── 4. Date overrides ──────────────────────────────────────────── */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <CalendarX2 className="w-5 h-5 text-primary" /> Date Overrides
                    </CardTitle>
                    <CardDescription className="mt-1.5">
                      Block specific dates entirely, or set custom hours for a single day.
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setOverrideForm({ date: "", unavailable: true, start: "09:00", end: "12:00" });
                      setOverrideOpen(true);
                    }}
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add override
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {data.dateOverrides.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No date overrides.</p>
                ) : (
                  <div className="space-y-1.5">
                    {data.dateOverrides.map((o: DateOverride) => (
                      <div
                        key={o.id}
                        className="flex items-center justify-between gap-3 py-1.5 px-2 rounded-md hover:bg-muted/40"
                      >
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="text-sm font-medium">{formatDate(o.date)}</span>
                          {o.is_unavailable ? (
                            <Badge variant="secondary" className="bg-rose-100 text-rose-700 hover:bg-rose-100">
                              Unavailable
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="bg-blue-100 text-blue-700 hover:bg-blue-100">
                              Custom: {formatHHMM(o.start_time)}–{formatHHMM(o.end_time)}
                            </Badge>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive hover:text-destructive shrink-0"
                          onClick={() => deleteOverrideMutation.mutate(o.id)}
                          disabled={deleteOverrideMutation.isPending}
                          title="Delete override"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ── 5. Event types ─────────────────────────────────────────────── */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <CalendarClock className="w-5 h-5 text-primary" /> Event Types
                    </CardTitle>
                    <CardDescription className="mt-1.5">
                      The meeting types invitees can book with you.
                    </CardDescription>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => {
                      setEditingEventType(null);
                      setEditorOpen(true);
                    }}
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add event type
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {data.eventTypes.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No event types yet — add one so invitees have something to book.
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {data.eventTypes.map((et) => (
                      <div
                        key={et.id}
                        className="flex items-center justify-between gap-3 py-2 px-2 rounded-md hover:bg-muted/40"
                      >
                        <div className="flex items-center gap-3 flex-wrap min-w-0">
                          <span className="text-sm font-medium truncate">{et.name}</span>
                          <Badge variant="outline">{et.duration_min} min</Badge>
                          <Badge variant="outline">{locationLabel(et.location_type)}</Badge>
                          {et.is_active ? (
                            <Badge className="bg-green-100 text-green-800 hover:bg-green-100 border border-green-300">
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="secondary">Inactive</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => {
                              setEditingEventType(et);
                              setEditorOpen(true);
                            }}
                            title="Edit"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            onClick={() => setDeletingEventType(et)}
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ── 6. Embed snippet ───────────────────────────────────────────── */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Code2 className="w-5 h-5 text-primary" /> Embed on a Website
                </CardTitle>
                <CardDescription>
                  Paste this snippet into any web page to embed your booking page.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Textarea readOnly value={embedSnippet} rows={3} className="font-mono text-xs" />
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-xs text-muted-foreground">
                    Note: the snippet uses this deployment's URL, which works today;
                    once DNS points the <span className="font-mono">book.carpentercpas.com</span> vanity
                    domain at the deployment, you can swap it into the snippet.
                  </p>
                  <CopyButton text={embedSnippet} label="Copy snippet" />
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* ── Add override dialog ──────────────────────────────────────────── */}
        <Dialog open={overrideOpen} onOpenChange={setOverrideOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add date override</DialogTitle>
              <DialogDescription>
                Block a specific date entirely, or give it custom hours.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="ov-date">Date</Label>
                <Input
                  id="ov-date"
                  type="date"
                  value={overrideForm.date}
                  onChange={(e) => setOverrideForm((f) => ({ ...f, date: e.target.value }))}
                />
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  id="ov-unavailable"
                  checked={overrideForm.unavailable}
                  onCheckedChange={(v) => setOverrideForm((f) => ({ ...f, unavailable: v }))}
                />
                <Label htmlFor="ov-unavailable" className="cursor-pointer">
                  Unavailable all day
                </Label>
              </div>
              {!overrideForm.unavailable && (
                <div className="flex items-center gap-2">
                  <Input
                    type="time"
                    value={overrideForm.start}
                    onChange={(e) => setOverrideForm((f) => ({ ...f, start: e.target.value }))}
                    className="w-32"
                  />
                  <span className="text-muted-foreground text-sm">to</span>
                  <Input
                    type="time"
                    value={overrideForm.end}
                    onChange={(e) => setOverrideForm((f) => ({ ...f, end: e.target.value }))}
                    className="w-32"
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOverrideOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (!overrideForm.date) {
                    toast({ title: "Pick a date", variant: "destructive" });
                    return;
                  }
                  if (!overrideForm.unavailable && overrideForm.start >= overrideForm.end) {
                    toast({
                      title: "Check the times",
                      description: "Start time must be before end time.",
                      variant: "destructive",
                    });
                    return;
                  }
                  createOverrideMutation.mutate();
                }}
                disabled={createOverrideMutation.isPending}
              >
                {createOverrideMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Add override
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── Event type editor ────────────────────────────────────────────── */}
        <EventTypeEditor
          open={editorOpen}
          onOpenChange={setEditorOpen}
          eventType={editingEventType}
        />

        {/* ── Delete event type confirm ────────────────────────────────────── */}
        <AlertDialog
          open={!!deletingEventType}
          onOpenChange={(open) => !open && setDeletingEventType(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete "{deletingEventType?.name}"?</AlertDialogTitle>
              <AlertDialogDescription>
                Invitees will no longer be able to book this meeting type. Event types with
                existing bookings cannot be deleted — deactivate them instead.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className={buttonVariants({ variant: "destructive" })}
                onClick={() => {
                  if (deletingEventType) deleteEventTypeMutation.mutate(deletingEventType.id);
                  setDeletingEventType(null);
                }}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* ── Disconnect Google confirm ────────────────────────────────────── */}
        <AlertDialog open={confirmDisconnect} onOpenChange={setConfirmDisconnect}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Disconnect Google Calendar?</AlertDialogTitle>
              <AlertDialogDescription>
                Your Google events will no longer block booking slots — invitees could book
                over your Google commitments.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className={buttonVariants({ variant: "destructive" })}
                onClick={() => disconnectGoogleMutation.mutate()}
              >
                Disconnect
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
